import React, { useMemo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Hotel } from "../../types/hotel.d";

interface HotelSelectorProps {
    hotels: Hotel[];
    selectedHotel: Hotel | null; // null also represents "all" when includeAllOption=true
    onChange: (hotel: Hotel | null) => void;
    disabled?: boolean;
    includeAllOption?: boolean;
    allLabel?: string;
}

// Internal wrapper type to support a synthetic "all" choice
type OptionType = Hotel & { __all__?: boolean };

const HotelSelector: React.FC<HotelSelectorProps> = ({
    hotels,
    selectedHotel,
    onChange,
    disabled,
    includeAllOption = false,
    allLabel = "Tous les hôtels",
}) => {
    const options: OptionType[] = useMemo(() => {
        if (!includeAllOption) return hotels;
        return [
            { id: -1, name: allLabel, maxUnitsCapacity: 0, __all__: true } as OptionType,
            ...hotels,
        ];
    }, [hotels, includeAllOption, allLabel]);

        const value: OptionType | null = useMemo(() => {
            if (!includeAllOption) return (selectedHotel as OptionType | null) ?? null;
            if (selectedHotel == null) {
                // Represent "all" by the synthetic option
                return (options.find((o) => (o as OptionType).__all__) as OptionType) ?? null;
            }
            return (options.find((o) => o.id === selectedHotel.id) as OptionType) ?? null;
        }, [selectedHotel, options, includeAllOption]);

    return (
        <Autocomplete
            getOptionLabel={(opt) => opt.name}
            options={options}
            renderInput={(params) => <TextField {...params} label="Hôtel" />}
            sx={{ width: 275, mb: 5 }}
            onChange={(_, value) => {
                if (includeAllOption && value && (value as OptionType).__all__) {
                    onChange(null); // null => all hotels
                } else {
                    onChange(value as Hotel | null);
                }
            }}
            value={value}
            disabled={disabled}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
        />
    );
};

export default HotelSelector;
